export interface ApiOriginConfig {
  dev: boolean;
  configuredUrl?: string;
}

const CONFIG_ERROR =
  'VITE_API_URL must be an absolute HTTP(S) origin with no credentials, path, query, or fragment';

export function resolveApiOrigin({ dev, configuredUrl }: ApiOriginConfig): string {
  if (dev) return '';

  const candidate = configuredUrl?.trim().replace(/\/+$/, '') ?? '';
  let parsed: URL;
  try {
    parsed = new URL(candidate);
  } catch {
    throw new Error(CONFIG_ERROR);
  }

  const hasOnlyOrigin =
    (parsed.protocol === 'http:' || parsed.protocol === 'https:')
    && parsed.username === ''
    && parsed.password === ''
    && parsed.pathname === '/'
    && parsed.search === ''
    && parsed.hash === '';
  if (!hasOnlyOrigin) throw new Error(CONFIG_ERROR);

  const isLoopback = ['localhost', '127.0.0.1', '[::1]'].includes(parsed.hostname.toLowerCase());
  if (parsed.protocol !== 'https:' && !isLoopback) {
    throw new Error('HTTPS is required for non-local VITE_API_URL origins');
  }

  return parsed.origin;
}

export function resolveBackendUrl(pathOrUrl: string, origin: string): string {
  if (/^(?:https?:)?\/\//i.test(pathOrUrl)) {
    if (!origin || pathOrUrl.startsWith('//')) {
      throw new Error('Backend URL must use the configured Product API origin');
    }
    const parsed = new URL(pathOrUrl);
    if (parsed.origin !== origin) {
      throw new Error('Backend URL must use the configured Product API origin');
    }
    return parsed.toString();
  }
  if (/^(?:data|blob):/i.test(pathOrUrl)) {
    throw new Error('Backend URL must use the configured Product API origin');
  }
  if (!origin) return pathOrUrl;
  return `${origin}${pathOrUrl.startsWith('/') ? '' : '/'}${pathOrUrl}`;
}

/** Reject Axios targets that could override the owned client baseURL. */
export function assertOwnedBackendRequest(
  pathOrUrl: string | undefined,
  requestBaseUrl: string | undefined,
  ownedOrigin: string,
): void {
  if ((requestBaseUrl ?? '') !== ownedOrigin) {
    throw new Error('Product request target must use the configured Product API origin');
  }
  if (!pathOrUrl) return;

  const trustedOrigin = ownedOrigin || globalThis.location?.origin;
  let resolved: URL;
  try {
    if (!trustedOrigin || trustedOrigin === 'null') throw new Error('missing Product origin');
    resolved = new URL(pathOrUrl, `${trustedOrigin}/`);
  } catch {
    throw new Error('Product request target must use the configured Product API origin');
  }
  if (
    (resolved.protocol !== 'http:' && resolved.protocol !== 'https:')
    || resolved.origin !== trustedOrigin
  ) {
    throw new Error('Product request target must use the configured Product API origin');
  }
}

/**
 * The sole Product backend-origin authority. Development intentionally returns
 * an empty string so Vite can proxy root-relative API and media requests.
 * Every non-development runtime fails closed when its backend origin is absent
 * or malformed.
 */
export function getProductApiOrigin(): string {
  return resolveApiOrigin({
    dev: import.meta.env.DEV,
    configuredUrl: import.meta.env.VITE_API_URL,
  });
}

export const productApiOrigin = getProductApiOrigin();
